<template>
  <div
    :class="className"
    :style="{ height: height, width: width }"
    ref="chart"
  ></div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'BarChart',
  props: {
    className: {
      type: String,
      default: 'chart'
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '400px'
    },
    // 数据格式: { xAxis: [], series: [{ name, data }] }
    chartData: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    color: {
      type: Array,
      default: () => ['#5B74DA', '#D98E5E', '#F7B500', '#6DD400', '#0091FF']
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    yAxisConfig: {
      type: Object,
      default: () => ({})
    },
    yAxisName: {
      type: String,
      default: ''
    },
    barWidth: {
      type: [String, Number],
      default: 30
    },
    showLegend: {
      type: Boolean,
      default: false
    },
    gridRight: {
      type: [String, Number],
      default: '20%'
    },
    gridLeft: {
      type: [String, Number],
      default: '20%'
    }
  },
  data() {
    return {
      chart: null
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler(val) {
        this.setOptions(val)
      }
    },
    yAxisConfig: {
      deep: true,
      handler() {
        this.setOptions(this.chartData)
      }
    },
    height() {
      this.$nextTick(() => {
        if (this.chart) this.chart.resize()
      })
    },
    width() {
      this.$nextTick(() => {
        if (this.chart) this.chart.resize()
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart()
    })
  },
  beforeDestroy() {
    if (!this.chart) {
      return
    }
    window.removeEventListener('resize', this.__resizeHandler)
    this.chart.dispose()
    this.chart = null
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
      this.setOptions(this.chartData)
      this.__resizeHandler = this.debounce(() => {
        if (this.chart) {
          this.chart.resize()
        }
      }, 100)
      window.addEventListener('resize', this.__resizeHandler)
    },
    debounce(func, wait) {
      let timeout
      return function() {
        const context = this
        const args = arguments
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(() => {
          func.apply(context, args)
        }, wait)
      }
    },
    setOptions({ xAxis, series } = {}) {
      if (!this.chart) return
      if (!xAxis || !series) {
        this.chart.setOption({
          yAxis: {
            type: 'value',
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            splitLine: {
              lineStyle: {
                type: 'dashed',
                color: '#eee'
              }
            },
            axisLabel: {
              color: '#333'
            },
            ...this.yAxisConfig
          }
        })
        return
      }

      const option = {
        title: {
          text: this.title,
          textStyle: {
            fontSize: 16,
            color: '#333'
          },
          top: 10,
          left: 10
        },
        color: this.color,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderWidth: 1,
          borderColor: '#eee',
          textStyle: {
            color: '#666'
          }
        },
        grid: {
          top: 60,
          left: this.gridLeft,
          right: this.gridRight,
          bottom: this.showLegend ? 90 : 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xAxis,
          axisLine: {
            lineStyle: {
              color: '#eee'
            }
          },
          axisLabel: {
            color: '#333',
            interval: 0,
            rotate: 35,
            formatter: (value) => {
              return value.length > 30 ? value.slice(0, 30) + '...' : value
            }
          },
          axisTick: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: this.yAxisName || undefined,
          nameLocation: 'middle',
          nameGap: 60,
          nameTextStyle: {
            color: '#333',
            fontSize: 14
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#eee'
            }
          },
          axisLabel: {
            color: '#333'
          },
          ...this.yAxisConfig
        },
        series: series.map((item) => ({
          name: item.name,
          type: 'bar',
          barWidth: this.barWidth,
          data: item.data,
          label: {
            show: this.showLabel,
            position: 'top',
            color: '#333'
          },
          itemStyle: {}
        }))
      }
      if (this.showLegend) {
        option.legend = {
          data: series.map((item) => item.name),
          bottom: 10,
          left: 'center',
          textStyle: {
            color: '#333'
          }
        }
      }
      this.chart.setOption(option, true)
    }
  }
}
</script>
